import { WSClient } from "./ws-client";
import type {
  Job,
  JobFeedbackInput,
  JobFeedbackMetadata,
  JobFeedbackRecord,
  JobFeedbackToolCall,
  JobStatus,
  Prompt,
  ToolWithHandler,
  ToolSchema,
  PublishToolsResult,
  ToolHandler,
  InstanceToolHandler,
  ToolInvokeParams,
  EffectHandlerContext,
  DomainState,
  JobSubmitResult,
  ToolInfo,
  ToolsChangedEvent,
  EffectInfo,
  EffectsChangedEvent,
  GranularQuotaProgress,
  UserMessageAppendResult,
  UserMessageInput,
} from "./types";

import type { Doc } from "@automerge/automerge/slim";
import { normalizePrompt, resolvePromptAnswer } from "./prompt-utils";
import {
  createFeedPublisher,
  SessionFeedController,
  type FeedPublisher,
  type FeedListTransport,
  type FeedSubscribeOptions,
  type FeedSubscriptionChange,
  type SessionFeedApi,
  type TransientFeedbackHandle,
} from "./feed";

function toPascalCase(value: string): string {
  return value
    .split(/[_:\-\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");
}

function reserveUserMessageId(): string {
  const randomUuid = globalThis.crypto?.randomUUID?.();
  return randomUuid
    ? `message_${randomUuid}`
    : `message_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function normalizeUserMessageIdentity(
  value: unknown,
  field: "id" | "operationId",
): string | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== "string" || !value.trim()) {
    throw new Error(`User message ${field} must be a non-empty string.`);
  }
  return value.trim();
}

function recordFromUnknown(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function promptSnapshotFingerprint(prompt: Prompt): string {
  return JSON.stringify({
    id: prompt.id,
    jobId: prompt.jobId || null,
    turnId: prompt.turnId || null,
    type: prompt.type,
    title: prompt.title,
    message: prompt.message,
    options: prompt.options || null,
    defaultValue: prompt.defaultValue,
    placeholder: prompt.placeholder || null,
    allowEmpty: prompt.allowEmpty,
    metadata: prompt.metadata || null,
  });
}

// Re-export types that were previously defined here
export type {
  ToolWithHandler,
  ToolSchema,
  PublishToolsResult,
  ToolHandler,
  InstanceToolHandler,
  DomainState,
  JobSubmitResult,
};

export class Session {
  protected client: WSClient;
  private clientId: string;
  private initialQuota: GranularQuotaProgress | null;
  private jobsMap: Map<string, JobImplementation> = new Map();
  private eventListeners: Map<string, Array<(data: unknown) => void>> =
    new Map();
  private toolHandlers: Map<string, ToolHandler | InstanceToolHandler> =
    new Map();
  /** Tracks which tools are instance methods (className set, not static) */
  private instanceTools: Set<string> = new Set();
  private currentDomainRevision: string | null = null;
  /** Local effect registry: name → full ToolWithHandler */
  private effects: Map<string, ToolWithHandler> = new Map();
  /** Last known tools for diffing */
  private lastKnownTools: Map<string, ToolInfo> = new Map();
  /** Last seen live prompts, keyed by prompt id, for answer normalization */
  private promptCache: Map<string, Prompt> = new Map();
  /** Prompt ids locally answered before the document sync catches up. */
  private hiddenPromptIds: Set<string> = new Set();
  private domainPackagePartCache: Map<"types" | "docs", string> = new Map();
  private domainPackagePartPromises: Map<"types" | "docs", Promise<string>> =
    new Map();
  private domainPackageFetchQueue: Promise<void> = Promise.resolve();
  private readonly feedController: SessionFeedController;
  readonly feed: SessionFeedApi;

  constructor(
    client: WSClient,
    clientId?: string,
    options: { initialQuota?: GranularQuotaProgress | null } = {},
  ) {
    this.client = client;
    this.clientId = clientId || `client_${Date.now()}`;
    this.initialQuota = options.initialQuota || null;
    this.feedController = new SessionFeedController(this.client.doc, {
      listTransport: (feedOptions) =>
        this.client.call(
          "feed.list",
          feedOptions,
        ) as ReturnType<FeedListTransport>,
    });
    this.feed = Object.freeze({
      getSnapshot: () => this.feedController.getSnapshot(),
      list: (feedOptions = {}) => this.feedController.list(feedOptions),
      subscribe: (
        listener: (change: FeedSubscriptionChange) => void,
        subscribeOptions: FeedSubscribeOptions = {},
      ) => this.feedController.subscribe(listener, subscribeOptions),
    });
    this.setupEventHandlers();
    this.setupToolInvokeHandler();
    this.currentDomainRevision = this.extractDomainRevisionFromDoc(
      this.client.doc,
    );
  }

  private extractDomainRevisionFromDoc(doc: unknown): string | null {
    const domain = (doc as { domain?: { active?: unknown } } | null | undefined)
      ?.domain;
    const active = domain?.active;
    if (typeof active === "string" && active.trim()) {
      return active;
    }
    const nestedRevision = (
      active as { domainRevision?: unknown } | null | undefined
    )?.domainRevision;
    if (typeof nestedRevision === "string" && nestedRevision.trim()) {
      return nestedRevision;
    }
    return null;
  }

  /**
   * Prompt delivery is deliberately event-driven while a socket is live, but
   * an existing session can be attached from a fresh tab/client after the
   * event was originally sent. Rebuild that client's prompt cache from the
   * canonical document on every sync so an open durable prompt remains
   * actionable after reconnect without manufacturing a second prompt.
   */
  private reconcilePromptCacheFromCanonicalDocument(doc: unknown): void {
    const jobsById = recordFromUnknown(recordFromUnknown(doc)?.jobs)?.byId;
    const jobs = recordFromUnknown(jobsById) || {};
    const openPromptIds = new Set<string>();

    for (const [jobId, jobValue] of Object.entries(jobs)) {
      const prompts = recordFromUnknown(recordFromUnknown(jobValue)?.prompts);
      if (!prompts) continue;
      for (const [promptId, promptValue] of Object.entries(prompts)) {
        const persisted = recordFromUnknown(promptValue);
        if (!persisted || persisted.status !== "open") continue;
        const prompt = normalizePrompt({
          promptId,
          jobId,
          kind: persisted.kind,
          type: persisted.type,
          title: persisted.title,
          message: persisted.message,
          options: persisted.options,
          defaultValue: persisted.defaultValue,
          placeholder: persisted.placeholder,
          allowEmpty: persisted.allowEmpty,
          metadata: persisted.metadata,
        });
        if (!prompt) continue;
        openPromptIds.add(prompt.id);
        if (this.hiddenPromptIds.has(prompt.id)) continue;
        const previous = this.promptCache.get(prompt.id);
        this.promptCache.set(prompt.id, prompt);
        if (
          !previous ||
          promptSnapshotFingerprint(previous) !==
            promptSnapshotFingerprint(prompt)
        ) {
          this.emit("prompt", prompt);
        }
      }
    }

    for (const promptId of this.promptCache.keys()) {
      if (!openPromptIds.has(promptId)) {
        this.promptCache.delete(promptId);
      }
    }
  }

  private buildDirectedInvocationEffectContext(
    params: ToolInvokeParams,
    feedbackContext: {
      feedback: FeedPublisher["feedback"];
      transientFeedback: FeedPublisher["transientFeedback"];
    } | null,
  ): EffectHandlerContext {
    const commitUnavailable = async (): Promise<never> => {
      throw new Error(
        "This directed browser tool invocation has no product commit transport",
      );
    };
    return {
      effectClientId: this.clientId,
      sandboxId: params.sandboxId || "",
      environmentId: params.environmentId || "",
      invocationId: params.callId,
      jobId: params.jobId,
      sessionId: params.sessionId || this.client.currentSessionId,
      user: {
        granularId: "",
        userId: "",
        subjectId: "",
      },
      commit: {
        effect: commitUnavailable,
        transition: commitUnavailable,
      },
      ...(feedbackContext
        ? {
            feedback: feedbackContext.feedback,
            transientFeedback: feedbackContext.transientFeedback,
          }
        : {}),
    };
  }

  private createDirectedInvocationFeedbackContext(params: ToolInvokeParams): {
    feedback: FeedPublisher["feedback"];
    transientFeedback: FeedPublisher["transientFeedback"];
    flush: () => Promise<void>;
  } | null {
    if (!params.feedbackCapability) return null;

    const publications: Promise<unknown>[] = [];
    let nextOperationOrdinal = 0;
    const nextOperationId = (kind: string): string =>
      `${kind}:${nextOperationOrdinal++}`;
    const track = <T>(publication: Promise<T>): Promise<T> => {
      const tracked = Promise.resolve(publication);
      publications.push(tracked);
      void tracked.catch(() => undefined);
      return tracked;
    };
    const methodMap: Record<string, string> = {
      "feed.feedback": "tool.feedback",
      "feed.transient.create": "tool.transient.create",
      "feed.transient.update": "tool.transient.update",
      "feed.transient.settle": "tool.transient.settle",
    };
    const publisher = createFeedPublisher((method, publishParams) => {
      const directedFeedbackMethod = methodMap[method];
      if (!directedFeedbackMethod) {
        throw new Error(`Unsupported directed feedback method: ${method}`);
      }
      return this.client.call(directedFeedbackMethod, {
        ...publishParams,
        callId: params.callId,
        feedbackCapability: params.feedbackCapability,
      });
    });

    const wrapTransientHandle = (
      initial: TransientFeedbackHandle,
    ): TransientFeedbackHandle => {
      let current = initial;
      const wrapped: TransientFeedbackHandle = {
        get id() {
          return current.id;
        },
        get ordinal() {
          return current.ordinal;
        },
        get revision() {
          return current.revision;
        },
        async update(text, options = {}) {
          current = await track(
            current.update(text, {
              ...options,
              operationId:
                options.operationId || nextOperationId("transient-update"),
            }),
          );
          return wrapped;
        },
        settle(text, options = {}) {
          return track(
            current.settle(text, {
              ...options,
              operationId:
                options.operationId || nextOperationId("transient-settle"),
            }),
          );
        },
      };
      return wrapped;
    };

    return {
      feedback: (text, options = {}) =>
        track(
          publisher.feedback(text, {
            ...options,
            operationId: options.operationId || nextOperationId("feedback"),
          }),
        ),
      transientFeedback: (text, options = {}) =>
        track(
          publisher
            .transientFeedback(text, {
              ...options,
              operationId:
                options.operationId || nextOperationId("transient-create"),
            })
            .then(wrapTransientHandle),
        ),
      async flush() {
        let cursor = 0;
        let firstError: unknown;
        while (cursor < publications.length) {
          const batch = publications.slice(cursor);
          cursor = publications.length;
          const results = await Promise.allSettled(batch);
          for (const result of results) {
            if (result.status === "rejected" && firstError === undefined) {
              firstError = result.reason;
            }
          }
        }
        if (firstError !== undefined) throw firstError;
      },
    };
  }

  // --- Public API ---

  get document(): Doc<Record<string, unknown>> {
    return this.client.doc;
  }

  get quota(): GranularQuotaProgress | null {
    return this.getQuota();
  }

  getQuota(): GranularQuotaProgress | null {
    const quota = (
      this.client.doc as {
        billing?: { quota?: unknown };
      }
    ).billing?.quota;
    if (quota && typeof quota === "object") {
      return quota as GranularQuotaProgress;
    }
    return this.initialQuota;
  }

  get sessionId(): string {
    return this.client.currentSessionId;
  }

  get domainRevision(): string | null {
    return this.currentDomainRevision;
  }

  /**
   * Make a raw RPC call to the session's Durable Object.
   *
   * Use this when you need to call an RPC method that doesn't have a
   * dedicated wrapper method on the Session/Environment class.
   *
   * @param method - RPC method name (e.g. 'domain.fetchPackagePart')
   * @param params - Request parameters
   * @returns The raw RPC response
   *
   * @example
   * ```typescript
   * const result = await env.rpc('domain.fetchPackagePart', {
   *   moduleSpecifier: '@sandbox/domain',
   *   part: 'types',
   * });
   * ```
   */
  async rpc<T = unknown>(
    method: string,
    params: Record<string, unknown> = {},
  ): Promise<T> {
    return this.client.call(method, params) as Promise<T>;
  }

  /**
   * Send client hello to establish the session
   */
  async hello(): Promise<{
    ok: boolean;
    environmentId?: string;
    docId?: string;
    graphContainerStatus?: {
      lastKeepAliveAt: number;
      status: "warming" | "hot" | "unknown";
    };
  }> {
    const result = await this.client.call("client.hello", {
      clientId: this.clientId,
      protocolVersion: "2.0",
    });
    return result as {
      ok: boolean;
      environmentId?: string;
      docId?: string;
      graphContainerStatus?: {
        lastKeepAliveAt: number;
        status: "warming" | "hot" | "unknown";
      };
    };
  }

  async publishTools(
    tools: ToolWithHandler[],
    revision = "1.0.0",
  ): Promise<PublishToolsResult> {
    void tools;
    void revision;
    throw new Error(
      "Environment-scoped effect publication was removed. Declare effects in the manifest and register live handlers with granular.ontology(environment.sandboxId).effects.registerMany(effects).",
    );
  }

  async publishEffect(effect: ToolWithHandler): Promise<PublishToolsResult> {
    void effect;
    throw new Error(
      "Environment-scoped effect publication was removed. Use granular.ontology(environment.sandboxId).effects.register(effect).",
    );
  }

  async publishEffects(
    effects: ToolWithHandler[],
  ): Promise<PublishToolsResult> {
    void effects;
    throw new Error(
      "Environment-scoped effect publication was removed. Use granular.ontology(environment.sandboxId).effects.registerMany(effects).",
    );
  }

  async unpublishEffect(name: string): Promise<PublishToolsResult> {
    void name;
    throw new Error(
      "Environment-scoped effect publication was removed. Use granular.ontology(environment.sandboxId).effects.unregister(effectName).",
    );
  }

  async unpublishAllEffects(): Promise<PublishToolsResult> {
    throw new Error(
      "Environment-scoped effect publication was removed. Use granular.ontology(environment.sandboxId).effects.clear().",
    );
  }

  /**
   * Submit a job to execute code in the sandbox.
   *
   * The code can import typed classes from Harness v3 runtime modules:
   * ```typescript
   * import { Author } from "@granular/domain/Author";
   * import { Book } from "@granular/domain/Book";
   * import { global_search } from "@granular/actions/backend";
   *
   * const totalAuthors = await Author.count();
   * const firstAuthorsPage = await Author.page({ page: 1, perPage: 10, saveAs: 'recent_authors' });
   * const authors = firstAuthorsPage.items;
   * const tolkien = await Author.get({ path: 'author_tolkien' });
   * const bio = await tolkien.get_bio({ detailed: true });
   * const books = await tolkien.get_books();
   * for await (const author of Author.iterate({ perPage: 100, maxItems: 500 })) {
   *   console.log(author.id);
   * }
   * ```
   *
   * Effect calls (instance methods, static methods, global functions) trigger
   * `effect.invoke` RPC back to the sandbox effect host, where the registered handlers
   * execute locally and return the result to the sandbox.
   */
  async submitJob(
    code: string,
    domainRevisionOrOptions?:
      | string
      | {
          domainRevision?: string;
          metadata?: Record<string, unknown>;
          agent?: Record<string, unknown>;
        },
  ): Promise<Job> {
    const options =
      typeof domainRevisionOrOptions === "string"
        ? { domainRevision: domainRevisionOrOptions }
        : domainRevisionOrOptions || {};
    let revision =
      options.domainRevision ||
      this.currentDomainRevision ||
      this.extractDomainRevisionFromDoc(this.client.doc) ||
      undefined;

    if (!revision) {
      try {
        const summary = await this.getDomain();
        revision =
          summary.activeDomainRevision ||
          this.extractDomainRevisionFromDoc(this.client.doc) ||
          undefined;
      } catch {
        // Fall through to the existing error below.
      }
    }

    if (!revision) {
      throw new Error(
        "No domain revision available. Register live effects or ensure the build schema is activated.",
      );
    }

    const result = (await this.client.call("job.submit", {
      domainRevision: revision,
      code,
      metadata: options.metadata,
      agent: options.agent,
    })) as { jobId?: string };

    if (!result.jobId) {
      throw new Error("Failed to submit job: no jobId returned");
    }

    const job = new JobImplementation(result.jobId, this.client, {
      code,
      domainRevision: revision,
      createdAt: Date.now(),
    });
    this.jobsMap.set(result.jobId, job);
    return job;
  }

  /**
   * Register a handler for a specific tool.
   * @param isInstance - If true, handler will receive (id, params) for instance method dispatch.
   */
  registerToolHandler(
    name: string,
    handler: ToolHandler | InstanceToolHandler,
    isInstance = false,
  ): void {
    this.toolHandlers.set(name, handler);
    if (isInstance) {
      this.instanceTools.add(name);
    } else {
      this.instanceTools.delete(name);
    }
  }

  /**
   * Respond to a prompt request from the sandbox
   */
  async answerPrompt(promptId: string, answer: unknown): Promise<void> {
    const prompt = this.promptCache.get(promptId);
    const resolvedAnswer = resolvePromptAnswer(prompt, answer);
    const response = await this.client.call("prompt.answer", {
      promptId,
      answer: resolvedAnswer,
      value: resolvedAnswer,
    });
    if (
      response &&
      typeof response === "object" &&
      "ok" in response &&
      (response as { ok?: unknown }).ok === false
    ) {
      const rejected = response as { error?: unknown };
      const errorMessage =
        typeof rejected.error === "string"
          ? rejected.error
          : "Prompt answer was rejected.";
      throw new Error(errorMessage);
    }

    // `prompt.answer` is the single authoritative operation. The server owns
    // the atomic prompt-state, typed user-message and transient cleanup write.
    this.promptCache.delete(promptId);
    this.hiddenPromptIds.add(promptId);
    this.emit("prompt:answered", {
      ...(prompt || { id: promptId }),
      id: promptId,
      answer: resolvedAnswer,
      status: "answered",
    });
  }

  async appendUserMessage(
    input: UserMessageInput,
  ): Promise<UserMessageAppendResult> {
    const requestedId = normalizeUserMessageIdentity(input.id, "id");
    const requestedOperationId = normalizeUserMessageIdentity(
      input.operationId,
      "operationId",
    );
    // Reserve the default wire identity before invoking the transport. For an
    // explicit operation-id-only call, omit id and let the canonical server
    // derive its deterministic message id in a separate identity namespace.
    const id =
      requestedId ||
      (requestedOperationId ? undefined : reserveUserMessageId());
    const operationId = requestedOperationId || `conversation.append:${id!}`;
    const {
      id: _ignoredInputId,
      operationId: _ignoredInputOperationId,
      ...message
    } = input;
    return this.client.call("conversation.append", {
      ...message,
      role: "user",
      ...(id ? { id } : {}),
      operationId,
    }) as Promise<UserMessageAppendResult>;
  }

  /**
   * Get the current list of available effects.
   * Consolidates effect declarations and live availability for the session.
   */
  getEffects(): EffectInfo[] {
    const doc: any = this.client.doc;
    const toolMap = new Map<string, ToolInfo>();

    // 1. Seed from domain packages (always present after domain activation)
    const domainPackages = doc.domain?.packages;
    const packageCandidates =
      domainPackages && typeof domainPackages === "object"
        ? [
            domainPackages.domain,
            domainPackages["@sandbox/domain"],
            ...Object.values(domainPackages),
          ].filter(Boolean)
        : [];
    for (const domainPkg of packageCandidates) {
      if (domainPkg?.tools && Array.isArray(domainPkg.tools)) {
        for (const tool of domainPkg.tools) {
          if (!tool?.name || toolMap.has(tool.name)) continue;
          toolMap.set(tool.name, {
            name: tool.name,
            description: tool.description,
            inputSchema: tool.inputSchema,
            outputSchema: tool.outputSchema,
            className: tool.className || undefined,
            static: tool.static || false,
            ready: false,
            publishedAt: undefined,
          });
        }
      }
      if (!domainPkg?.classes || typeof domainPkg.classes !== "object") {
        continue;
      }
      for (const [className, classDef] of Object.entries(
        domainPkg.classes as Record<string, any>,
      )) {
        const methods = Array.isArray(classDef?.methods)
          ? classDef.methods
          : [];
        for (const method of methods) {
          if (!method?.name || toolMap.has(method.name)) continue;
          toolMap.set(method.name, {
            name: method.name,
            description: method.description,
            inputSchema: method.inputSchema,
            outputSchema: method.outputSchema,
            className: method.className || classDef?.name || className,
            static: method.static || false,
            ready: false,
            publishedAt: undefined,
          });
        }
      }
    }
    const legacyDomainPkg = doc.domain?.packages?.domain;
    if (legacyDomainPkg?.tools && Array.isArray(legacyDomainPkg.tools)) {
      for (const tool of legacyDomainPkg.tools) {
        if (!tool?.name) continue;
        toolMap.set(tool.name, {
          name: tool.name,
          description: tool.description,
          inputSchema: tool.inputSchema,
          outputSchema: tool.outputSchema,
          className: tool.className || undefined,
          static: tool.static || false,
          ready: false,
          publishedAt: undefined,
        });
      }
    }
    if (
      legacyDomainPkg?.classes &&
      typeof legacyDomainPkg.classes === "object"
    ) {
      for (const [className, classDef] of Object.entries(
        legacyDomainPkg.classes as Record<string, any>,
      )) {
        const methods = Array.isArray(classDef?.methods)
          ? classDef.methods
          : [];
        for (const method of methods) {
          if (!method?.name || toolMap.has(method.name)) continue;
          toolMap.set(method.name, {
            name: method.name,
            description: method.description,
            inputSchema: method.inputSchema,
            outputSchema: method.outputSchema,
            className: method.className || classDef?.name || className,
            static: method.static || false,
            ready: false,
            publishedAt: undefined,
          });
        }
      }
    }
    const hasPolicyFilteredDomainTools = toolMap.size > 0;

    // 2. Overlay with live catalogs (has handler readiness info)
    const catalogs = doc.catalog?.rawToolCatalogs || {};
    for (const [clientId, catalog] of Object.entries(catalogs)) {
      const cat = catalog as any;
      if (!cat.tools) continue;

      for (const tool of cat.tools) {
        if (!tool?.name) continue;
        const existing = toolMap.get(tool.name);
        if (hasPolicyFilteredDomainTools && !existing) continue;
        // Skip if we already have a newer catalog entry
        if (
          existing?.publishedAt &&
          cat.publishedAt &&
          existing.publishedAt > cat.publishedAt
        )
          continue;

        const isLocal = clientId === this.clientId;
        const ready = isLocal
          ? this.effects.has(tool.name) ||
            this.toolHandlers.has(tool.name) ||
            this.instanceTools.has(tool.name)
          : true;

        toolMap.set(tool.name, {
          name: tool.name,
          description: tool.description,
          inputSchema: tool.inputSchema,
          outputSchema: tool.outputSchema,
          className: tool.className || existing?.className,
          static: tool.static || existing?.static || false,
          clientId,
          ready,
          publishedAt: cat.publishedAt,
        });
      }
    }

    return Array.from(toolMap.values());
  }

  /**
   * Return the currently open prompt payloads known to this session.
   *
   * These come from live `prompt` / `prompt.request` websocket events and
   * preserve the exact shape used by `answerPrompt(...)`.
   */
  getPrompts(): Prompt[] {
    return Array.from(this.promptCache.values()).map((prompt) => ({
      ...prompt,
      options: Array.isArray(prompt.options)
        ? prompt.options.map((option) =>
            typeof option === "string" ? option : { ...option },
          )
        : undefined,
      metadata: prompt.metadata ? { ...prompt.metadata } : undefined,
    }));
  }

  getHiddenPromptIds(): string[] {
    return Array.from(this.hiddenPromptIds);
  }

  /**
   * Backwards-compatible alias for `getEffects()`.
   */
  getTools(): ToolInfo[] {
    return this.getEffects();
  }

  /**
   * Subscribe to effect changes (added, removed, updated).
   * @param callback - Function called with change events
   * @returns Unsubscribe function
   */
  onEffectsChanged(callback: (event: EffectsChangedEvent) => void): () => void {
    const handler = (data: unknown) => callback(data as EffectsChangedEvent);
    if (!this.eventListeners.has("effects:changed")) {
      this.eventListeners.set("effects:changed", []);
    }
    this.eventListeners.get("effects:changed")!.push(handler);

    return () => {
      const listeners = this.eventListeners.get("effects:changed");
      if (listeners) {
        this.eventListeners.set(
          "effects:changed",
          listeners.filter((h) => h !== handler),
        );
      }
    };
  }

  /**
   * Backwards-compatible alias for `onEffectsChanged()`.
   */
  onToolsChanged(callback: (event: ToolsChangedEvent) => void): () => void {
    const handler = (data: unknown) => callback(data as ToolsChangedEvent);
    if (!this.eventListeners.has("tools:changed")) {
      this.eventListeners.set("tools:changed", []);
    }
    this.eventListeners.get("tools:changed")!.push(handler);

    // Return unsubscribe function
    return () => {
      const listeners = this.eventListeners.get("tools:changed");
      if (listeners) {
        this.eventListeners.set(
          "tools:changed",
          listeners.filter((h) => h !== handler),
        );
      }
    };
  }

  /**
   * Get the current domain state and available tools
   */
  async getDomain(): Promise<DomainState> {
    const summary = (await this.client.call(
      "domain.getSummary",
      {},
    )) as DomainState;
    if (summary.activeDomainRevision) {
      this.currentDomainRevision = summary.activeDomainRevision;
    } else {
      this.currentDomainRevision = this.extractDomainRevisionFromDoc(
        this.client.doc,
      );
    }
    return summary;
  }

  /**
   * Fetch a domain package part from the backend (no fallback).
   */
  private async fetchDomainPart(part: "types" | "docs"): Promise<string> {
    const cached = this.domainPackagePartCache.get(part);
    if (cached !== undefined) {
      return cached;
    }

    const inFlight = this.domainPackagePartPromises.get(part);
    if (inFlight) {
      return inFlight;
    }

    const fetchPromise = this.domainPackageFetchQueue.then(async () => {
      const result = (await this.client.call("domain.fetchPackagePart", {
        moduleSpecifier: "@sandbox/domain",
        part,
      })) as { content?: string };
      const content = result?.content ?? "";
      this.domainPackagePartCache.set(part, content);
      return content;
    });

    this.domainPackagePartPromises.set(part, fetchPromise);
    this.domainPackageFetchQueue = fetchPromise.then(
      () => undefined,
      () => undefined,
    );

    try {
      return await fetchPromise;
    } finally {
      this.domainPackagePartPromises.delete(part);
    }
  }

  /**
   * Get TypeScript class declarations for the current domain (for LLM/code gen).
   */
  async getDomainTypes(): Promise<string> {
    return this.fetchDomainPart("types");
  }

  /**
   * Get Markdown documentation for the current domain (human-readable).
   */
  async getDomainDocs(): Promise<string> {
    return this.fetchDomainPart("docs");
  }

  /**
   * Get domain documentation for LLMs. Returns types (preferred) or fallback.
   */
  async getDomainDocumentation(): Promise<string> {
    const [types, docs] = await Promise.all([
      this.getDomainTypes(),
      this.getDomainDocs(),
    ]);
    const normalizedTypes = types.trim();
    const normalizedDocs = docs.trim();
    if (
      normalizedTypes &&
      (normalizedTypes.includes("export declare class") ||
        normalizedTypes.includes("export async function"))
    ) {
      if (!normalizedDocs) {
        return normalizedTypes;
      }
      return [normalizedTypes, "[Docs]", normalizedDocs].join("\n\n");
    }
    if (normalizedDocs) {
      return normalizedDocs;
    }
    const summary = await this.getDomain();
    return this.generateFallbackDocs(summary);
  }

  /**
   * Generate markdown documentation from the domain summary.
   * Class-aware: groups tools by class with property/relationship info.
   */
  private generateFallbackDocs(summary: DomainState): string {
    const classes = (summary as any).classes as Record<string, any> | undefined;
    const globalTools = (summary as any).globalTools as any[] | undefined;
    const tools = summary.tools || [];

    // If we have class data, produce class-aware docs
    if (classes && Object.keys(classes).length > 0) {
      let docs = "# Domain Documentation\n\n";
      docs +=
        "Import concrete classes from `@granular/domain/<Class>` and global backend actions from `@granular/actions/backend`:\n\n";

      const classNames = Object.keys(classes).map(toPascalCase);
      const globalNames = (globalTools || []).map((t: any) => t.name);
      const importLines = [
        ...classNames.map(
          (name) => `import { ${name} } from "@granular/domain/${name}";`,
        ),
        globalNames.length > 0
          ? `import { ${globalNames.join(", ")} } from "@granular/actions/backend";`
          : null,
      ].filter(Boolean);
      docs += `\`\`\`typescript\n${importLines.join("\n") || "// No generated domain imports available."}\n\`\`\`\n\n`;

      for (const [className, cls] of Object.entries(classes)) {
        const TsName = toPascalCase(className);
        docs += `## ${TsName}\n\n`;
        if (cls.description) docs += `${cls.description}\n\n`;

        if (cls.properties?.length > 0) {
          docs += "**Properties:**\n";
          for (const p of cls.properties) {
            docs += `- \`${p.name}\`${p.description ? ": " + p.description : ""}\n`;
          }
          docs += "\n";
        }

        if (cls.relationships?.length > 0) {
          docs += "**Relationships:**\n";
          for (const r of cls.relationships) {
            const fModel =
              r.foreignModel?.charAt(0).toUpperCase() +
              r.foreignModel?.slice(1);
            docs += `- \`${r.localSubmodel}\` → ${fModel} (${r.kind})\n`;
          }
          docs += "\n";
        }

        if (cls.methods?.length > 0) {
          docs += "**Methods:**\n";
          for (const m of cls.methods) {
            docs += `- \`${TsName}.${m.name}(input)\``;
            if (m.description) docs += `: ${m.description}`;
            docs += "\n";
            if (m.inputSchema?.properties) {
              docs +=
                "  ```json\n  " +
                JSON.stringify(m.inputSchema, null, 2).replace(/\n/g, "\n  ") +
                "\n  ```\n";
            }
          }
          docs += "\n";
        }
      }

      if (globalTools && globalTools.length > 0) {
        docs += "## Global Effects\n\n";
        for (const tool of globalTools) {
          docs += `### ${tool.name}\n\n`;
          if (tool.description) docs += `${tool.description}\n\n`;
          if (tool.inputSchema) {
            docs += "**Input Schema:**\n```json\n";
            docs += JSON.stringify(tool.inputSchema, null, 2);
            docs += "\n```\n\n";
          }
        }
      }

      return docs;
    }

    // Legacy flat fallback
    if (!tools || tools.length === 0) {
      return "No effects available in this domain.";
    }

    let docs = "# Available Effects\n\n";
    docs +=
      "Import global backend actions from `@granular/actions/backend` and call them with await:\n\n";
    docs += `\`\`\`typescript\nimport { ${tools[0]?.name || "example"} } from "@granular/actions/backend";\n\n`;
    docs += "// Example:\n";
    docs += `const result = await ${tools[0]?.name || "example"}(input);\n`;
    docs += "```\n\n";

    for (const tool of tools) {
      docs += `## ${tool.name}\n\n`;
      if (tool.description) {
        docs += `${tool.description}\n\n`;
      }
      if (tool.inputSchema) {
        docs += "**Input Schema:**\n```json\n";
        docs += JSON.stringify(tool.inputSchema, null, 2);
        docs += "\n```\n\n";
      }
      if (tool.outputSchema) {
        docs += "**Output Schema:**\n```json\n";
        docs += JSON.stringify(tool.outputSchema, null, 2);
        docs += "\n```\n\n";
      }
    }

    return docs;
  }

  /**
   * Close the session and disconnect from the sandbox
   */
  async disconnect(): Promise<void> {
    this.disposeSessionFeed();
    try {
      await this.client.call("client.goodbye", {
        clientId: this.clientId,
        timestamp: Date.now(),
      });
    } catch (error) {
      // Best-effort goodbye: abrupt network failures can prevent RPC.
    }
    this.client.disconnect();
  }

  /** Stop feed repair work without detaching a reconnectable transport. */
  protected disposeSessionFeed(): void {
    this.feedController.dispose();
  }

  // --- Event Handling ---

  /**
   * Subscribe to session events
   */
  on(event: string, handler: (data: unknown) => void): () => void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event)!.push(handler);
    return () => this.off(event, handler);
  }

  /**
   * Unsubscribe from session events
   */
  off(event: string, handler: (data: unknown) => void): void {
    const handlers = this.eventListeners.get(event);
    if (handlers) {
      this.eventListeners.set(
        event,
        handlers.filter((h) => h !== handler),
      );
    }
  }

  // --- Internal ---

  protected setFeedListTransport(transport: FeedListTransport | null): void {
    this.feedController.setListTransport(transport);
  }

  private setupToolInvokeHandler(): void {
    this.client.registerRpcHandler("tool.invoke", async (params: unknown) => {
      const invocation = params as ToolInvokeParams;
      const { callId, toolName, input, feedbackCapability } = invocation;
      const capabilityResultParams = feedbackCapability
        ? { feedbackCapability }
        : {};

      this.emit("effect:invoke", {
        callId,
        effectKey: toolName,
        toolName,
        input,
      });
      this.emit("tool:invoke", { callId, toolName, input });

      const handler = this.toolHandlers.get(toolName);
      if (!handler) {
        // Send error result back
        await this.client.call("tool.result", {
          callId,
          ...capabilityResultParams,
          error: {
            code: "TOOL_NOT_FOUND",
            message: `Tool handler not found: ${toolName}`,
          },
        });
        return;
      }

      try {
        let result: unknown;
        const feedbackContext =
          this.createDirectedInvocationFeedbackContext(invocation);
        const invocationContext = this.buildDirectedInvocationEffectContext(
          invocation,
          feedbackContext,
        );
        let handlerError: unknown;
        let handlerFailed = false;
        // Instance methods receive _objectId from the sandbox runtime
        try {
          if (
            this.instanceTools.has(toolName) &&
            input &&
            typeof input === "object" &&
            "_objectId" in input
          ) {
            const { _objectId, ...restParams } = input as {
              _objectId: string;
              [key: string]: any;
            };
            result = await (handler as InstanceToolHandler)(
              _objectId,
              restParams,
              invocationContext,
            );
          } else {
            result = await (handler as ToolHandler)(input, invocationContext);
          }
        } catch (error) {
          handlerFailed = true;
          handlerError = error;
        }

        let feedbackError: unknown;
        let feedbackFailed = false;
        if (feedbackContext) {
          try {
            await feedbackContext.flush();
          } catch (error) {
            feedbackFailed = true;
            feedbackError = error;
          }
        }
        if (handlerFailed) throw handlerError;
        if (feedbackFailed) throw feedbackError;

        this.emit("effect:result", { callId, effectKey: toolName, result });
        this.emit("tool:result", { callId, result });

        // Send result back to sandbox
        await this.client.call("tool.result", {
          callId,
          ...capabilityResultParams,
          result,
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        this.emit("effect:result", {
          callId,
          effectKey: toolName,
          error: errorMessage,
        });
        this.emit("tool:result", { callId, error: errorMessage });

        await this.client.call("tool.result", {
          callId,
          ...capabilityResultParams,
          error: { code: "TOOL_EXECUTION_FAILED", message: errorMessage },
        });
      }
    });
  }

  private setupEventHandlers(): void {
    // Pipe client events to session events
    this.client.on("open", (payload: unknown) =>
      this.emit("open", payload || {}),
    );
    this.client.on("sync", (doc: unknown) => {
      this.currentDomainRevision = this.extractDomainRevisionFromDoc(doc);
      this.feedController.updateDocument(doc);
      this.reconcilePromptCacheFromCanonicalDocument(doc);
      this.emit("sync", doc);
      this.checkForToolChanges();
    });
    this.client.onCanonicalDocumentQuarantine?.((quarantine) => {
      this.feedController.quarantineCanonicalReplacement(quarantine);
    });
    const emitPrompt = (payload: unknown) => {
      const prompt = normalizePrompt(payload);
      if (!prompt) return;
      if (this.hiddenPromptIds.has(prompt.id)) {
        this.emit("prompt:answered", { ...prompt, status: "answered" });
        return;
      }
      this.promptCache.set(prompt.id, prompt);
      this.emit("prompt", prompt);
    };
    this.client.on("prompt", emitPrompt);
    this.client.on("prompt.request", emitPrompt);
    this.client.on("disconnect", (payload: unknown) =>
      this.emit("disconnect", payload || {}),
    );
    this.client.on("reconnect_error", (payload: unknown) =>
      this.emit("reconnect_error", payload || {}),
    );

    // Job status updates
    this.client.on("job.status", (data: unknown) => {
      this.emit("job:status", data);
    });

    this.client.on("harness.ui_status", (data: unknown) => {
      this.emit("harness:ui_status", data);
    });

    this.client.on("harness.model_stream", (data: unknown) => {
      this.emit("harness:model_stream", data);
    });

    this.client.on("harness.text_response.delta", (data: unknown) => {
      this.emit("harness:text_response_delta", data);
    });

    // Exec events
    this.client.on("exec.completed", (data: unknown) => {
      this.emit("exec:completed", data);
    });
    this.client.on("exec.progress", (data: unknown) => {
      this.emit("exec:progress", data);
    });
  }

  protected emit(event: string, data: unknown): void {
    const handlers = this.eventListeners.get(event);
    if (handlers) {
      handlers.forEach((h) => h(data));
    }
  }

  /**
   * Check for changes in the effect catalog and emit change events if needed.
   */
  private checkForToolChanges() {
    const currentTools = this.getEffects();
    const currentMap = new Map(currentTools.map((t) => [t.name, t]));

    const added: string[] = [];
    const removed: string[] = [];

    // Check for added or updated tools
    for (const [name, tool] of currentMap) {
      if (!this.lastKnownTools.has(name)) {
        added.push(name);
      }
    }

    // Check for removed tools
    for (const name of this.lastKnownTools.keys()) {
      if (!currentMap.has(name)) {
        removed.push(name);
      }
    }

    if (added.length > 0 || removed.length > 0) {
      this.lastKnownTools = currentMap;
      this.emit("effects:changed", {
        effects: currentTools,
        tools: currentTools,
        added,
        removed,
      });
      this.emit("tools:changed", {
        tools: currentTools,
        added,
        removed,
      });
    }
  }
}

// --- Job Implementation ---

const MAX_FEEDBACK_STRING_LENGTH = 16_000;
const MAX_FEEDBACK_LOG_LINES = 100;
const MAX_FEEDBACK_TOOL_CALLS = 50;
const MAX_FEEDBACK_ARRAY_ITEMS = 50;
const MAX_FEEDBACK_OBJECT_KEYS = 50;
const MAX_FEEDBACK_DEPTH = 6;

type JsonSafeValue =
  | string
  | number
  | boolean
  | null
  | JsonSafeValue[]
  | { [key: string]: JsonSafeValue };

function truncateFeedbackString(
  value: string,
  maxLength = MAX_FEEDBACK_STRING_LENGTH,
): string {
  if (value.length <= maxLength) {
    return value;
  }
  return `${value.slice(0, maxLength)}...[truncated ${value.length - maxLength} chars]`;
}

function normalizeJobStatus(status: unknown): JobStatus {
  if (typeof status !== "string") {
    return "queued";
  }

  switch (status) {
    case "completed":
      return "succeeded";
    case "failed":
    case "running":
    case "queued":
    case "awaitingTool":
    case "awaitingHuman":
    case "succeeded":
    case "timeout":
    case "canceled":
      return status;
    default:
      return "queued";
  }
}

function sanitizeFeedbackValue(
  value: unknown,
  depth = 0,
  seen = new WeakSet<object>(),
): JsonSafeValue {
  if (depth >= MAX_FEEDBACK_DEPTH) {
    return "[Max depth reached]";
  }

  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === "string") {
    return truncateFeedbackString(value);
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  if (typeof value === "bigint") {
    return truncateFeedbackString(value.toString());
  }

  if (value instanceof Error) {
    return {
      name: truncateFeedbackString(value.name),
      message: truncateFeedbackString(value.message),
      stack: truncateFeedbackString(value.stack || ""),
    };
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (Array.isArray(value)) {
    return value
      .slice(0, MAX_FEEDBACK_ARRAY_ITEMS)
      .map((entry) => sanitizeFeedbackValue(entry, depth + 1, seen));
  }

  if (typeof value === "object") {
    if (seen.has(value as object)) {
      return "[Circular]";
    }
    seen.add(value as object);

    const entries = Object.entries(value as Record<string, unknown>).slice(
      0,
      MAX_FEEDBACK_OBJECT_KEYS,
    );
    const sanitized: Record<string, JsonSafeValue> = {};

    for (const [key, entryValue] of entries) {
      sanitized[key] = sanitizeFeedbackValue(entryValue, depth + 1, seen);
    }

    return sanitized;
  }

  return truncateFeedbackString(String(value));
}

class JobImplementation implements Job {
  public id: string;
  public client: WSClient;
  public status: JobStatus = "queued";

  private _resultPromise: Promise<unknown>;
  private _resolveResult!: (value: unknown) => void;
  private _rejectResult!: (reason: unknown) => void;
  private eventListeners: Map<string, Array<(data: unknown) => void>> =
    new Map();
  private resultSettled = false;
  private metadata: JobFeedbackMetadata;

  constructor(
    id: string,
    client: WSClient,
    initialState: {
      code: string;
      domainRevision?: string;
      createdAt: number;
    },
  ) {
    this.id = id;
    this.client = client;
    this.metadata = {
      source: "sdk",
      status: "queued",
      code: truncateFeedbackString(initialState.code),
      domainRevision: initialState.domainRevision,
      createdAt: initialState.createdAt,
      stdout: [],
      stderr: [],
      toolCalls: [],
    };

    this._resultPromise = new Promise((resolve, reject) => {
      this._resolveResult = resolve;
      this._rejectResult = reject;
    });

    // Listen to exec events
    this.client.on("exec.completed", (data: unknown) => {
      const execData = data as {
        execId?: string;
        jobId?: string;
        result?: unknown;
        error?: unknown;
      };
      if (execData.execId === id || execData.jobId === id) {
        if (execData.error) {
          this.finalize("failed", undefined, execData.error);
        } else {
          this.finalize("succeeded", execData.result, undefined, {
            hasResult: Object.prototype.hasOwnProperty.call(execData, "result"),
          });
        }
        this.emit("status", this.status);
      }
    });

    this.client.on("exec.progress", (data: unknown) => {
      const progressData = data as {
        execId?: string;
        jobId?: string;
        stdout?: string;
        stderr?: string;
      };
      if (progressData.execId === id || progressData.jobId === id) {
        if (progressData.stdout) {
          this.markStarted();
          this.metadata.stdout = [
            ...this.metadata.stdout,
            truncateFeedbackString(progressData.stdout),
          ].slice(-MAX_FEEDBACK_LOG_LINES);
          this.emit("stdout", progressData.stdout);
        }
        if (progressData.stderr) {
          this.markStarted();
          this.metadata.stderr = [
            ...this.metadata.stderr,
            truncateFeedbackString(progressData.stderr),
          ].slice(-MAX_FEEDBACK_LOG_LINES);
          this.emit("stderr", progressData.stderr);
        }
      }
    });

    // Also listen to specific job events if available
    this.client.on(`job.${id}.status`, (status: unknown) => {
      const normalizedStatus = normalizeJobStatus(status);
      this.status = normalizedStatus;
      this.metadata.status = normalizedStatus;
      if (normalizedStatus === "running") {
        this.markStarted();
      }
      if (
        normalizedStatus === "failed" ||
        normalizedStatus === "timeout" ||
        normalizedStatus === "canceled"
      ) {
        this.finalize(normalizedStatus);
      }
      this.emit("status", normalizedStatus);
    });

    this.client.on(`job.${id}.stdout`, (line: unknown) => {
      this.markStarted();
      this.metadata.stdout = [
        ...this.metadata.stdout,
        truncateFeedbackString(String(line)),
      ].slice(-MAX_FEEDBACK_LOG_LINES);
      this.emit("stdout", line);
    });

    this.client.on(`job.${id}.stderr`, (line: unknown) => {
      this.markStarted();
      this.metadata.stderr = [
        ...this.metadata.stderr,
        truncateFeedbackString(String(line)),
      ].slice(-MAX_FEEDBACK_LOG_LINES);
      this.emit("stderr", line);
    });

    this.client.on(`job.${id}.result`, (result: unknown) => {
      this.finalize("succeeded", result, undefined, { hasResult: true });
    });

    this.client.on(`job.${id}.error`, (error: unknown) => {
      this.finalize("failed", undefined, error);
    });

    this.client.on("job.status", (data: unknown) => {
      const jobData = data as { jobId?: string; status?: unknown };
      if (jobData.jobId !== id) {
        return;
      }
      const normalizedStatus = normalizeJobStatus(jobData.status);
      this.status = normalizedStatus;
      this.metadata.status = normalizedStatus;
      if (normalizedStatus === "running") {
        this.markStarted();
      }
      this.emit("status", normalizedStatus);
    });

    // Listen for job.completed and job.failed events (generic events from backend)
    this.client.on("job.completed", (data: unknown) => {
      const jobData = data as { jobId?: string; result?: unknown };
      if (jobData.jobId === id) {
        this.finalize("succeeded", jobData.result, undefined, {
          hasResult: true,
        });
        this.emit("status", this.status);
      }
    });

    this.client.on("job.failed", (data: unknown) => {
      const jobData = data as { jobId?: string; error?: unknown };
      if (jobData.jobId === id) {
        this.finalize(
          "failed",
          undefined,
          jobData.error || new Error("Job failed"),
        );
        this.emit("status", this.status);
      }
    });

    // Live tool call tracking (broadcast by backend to all clients)
    this.client.on("tool.call.start", (data: unknown) => {
      const d = data as {
        jobId?: string;
        callId?: string;
        toolName?: string;
        input?: unknown;
        timestamp?: number;
      };
      if (d.jobId === id) {
        this.metadata.toolCalls = this.upsertToolCall({
          callId: d.callId,
          toolName: d.toolName,
          input: sanitizeFeedbackValue(d.input),
          startedAt: d.timestamp || Date.now(),
        });
        this.emit("toolCallStart", {
          callId: d.callId,
          toolName: d.toolName,
          input: d.input,
          timestamp: d.timestamp,
        });
      }
    });

    this.client.on("tool.call.end", (data: unknown) => {
      const d = data as {
        jobId?: string;
        callId?: string;
        toolName?: string;
        result?: unknown;
        error?: string;
        durationMs?: number;
        timestamp?: number;
      };
      if (d.jobId === id) {
        const completedAt = d.timestamp || Date.now();
        this.metadata.toolCalls = this.upsertToolCall({
          callId: d.callId,
          toolName: d.toolName,
          output: sanitizeFeedbackValue(d.result),
          error: d.error ? truncateFeedbackString(d.error) : undefined,
          completedAt,
          durationMs:
            typeof d.durationMs === "number" ? d.durationMs : undefined,
        });
        this.emit("toolCallEnd", {
          callId: d.callId,
          toolName: d.toolName,
          result: d.result,
          error: d.error,
          durationMs: d.durationMs,
          timestamp: d.timestamp,
        });
      }
    });
  }

  get result(): Promise<unknown> {
    return this._resultPromise;
  }

  async leaveFeedback(input: JobFeedbackInput): Promise<JobFeedbackRecord> {
    const sentiment = input.sentiment;
    if (sentiment !== "good" && sentiment !== "bad") {
      throw new Error('Job feedback sentiment must be "good" or "bad".');
    }

    const comment =
      typeof input.comment === "string" ? input.comment.trim() : "";
    const response = (await this.client.call("job.feedback", {
      jobId: this.id,
      sentiment,
      comment: comment || undefined,
      metadata: this.buildFeedbackMetadata(),
    })) as JobFeedbackRecord;

    this.emit("feedback", response);
    return response;
  }

  on(event: string, handler: (data: unknown) => void): () => void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event)!.push(handler);

    return () => {
      const handlers = this.eventListeners.get(event);
      if (!handlers) {
        return;
      }
      this.eventListeners.set(
        event,
        handlers.filter((current) => current !== handler),
      );
    };
  }

  private buildFeedbackMetadata(): JobFeedbackMetadata {
    const startedAt = this.metadata.startedAt;
    const completedAt = this.metadata.completedAt;
    return {
      ...this.metadata,
      durationMs:
        typeof startedAt === "number" && typeof completedAt === "number"
          ? completedAt - startedAt
          : (this.metadata.durationMs ?? null),
      stdout: [...this.metadata.stdout],
      stderr: [...this.metadata.stderr],
      toolCalls: this.metadata.toolCalls
        .slice(0, MAX_FEEDBACK_TOOL_CALLS)
        .map((call) => ({ ...call })),
    };
  }

  private markStarted(timestamp = Date.now()): void {
    if (!this.metadata.startedAt) {
      this.metadata.startedAt = timestamp;
    }
    if (this.status === "queued") {
      this.status = "running";
    }
    if (this.metadata.status === "queued") {
      this.metadata.status = "running";
    }
  }

  private finalize(
    status: JobStatus,
    result?: unknown,
    error?: unknown,
    options: { hasResult?: boolean } = {},
  ): void {
    if (!this.metadata.startedAt) {
      this.metadata.startedAt = Date.now();
    }

    this.status = status;
    this.metadata.status = status;
    this.metadata.completedAt = this.metadata.completedAt || Date.now();
    this.metadata.durationMs =
      this.metadata.completedAt - this.metadata.startedAt;

    if (!this.resultSettled && (options.hasResult || result !== undefined)) {
      this.metadata.result = sanitizeFeedbackValue(result);
      this.resultSettled = true;
      this._resolveResult(result);
    }

    if (
      !this.resultSettled &&
      (error !== undefined ||
        status === "failed" ||
        status === "timeout" ||
        status === "canceled")
    ) {
      const fallbackError = new Error(`Job ${this.id} ${status}.`);
      const cause = error ?? fallbackError;
      const message = cause instanceof Error ? cause.message : String(cause);
      this.metadata.error = truncateFeedbackString(message);
      this.resultSettled = true;
      this._rejectResult(cause);
    }
  }

  private upsertToolCall(
    next: Partial<JobFeedbackToolCall> & { callId?: string; toolName?: string },
  ): JobFeedbackToolCall[] {
    const callId = next.callId || `tool-call-${Date.now()}`;
    const existingIndex = this.metadata.toolCalls.findIndex(
      (entry) => entry.callId === callId,
    );
    const existing =
      existingIndex >= 0 ? this.metadata.toolCalls[existingIndex] : undefined;
    const merged: JobFeedbackToolCall = {
      ...existing,
      ...next,
      callId,
      toolName: next.toolName || existing?.toolName,
    };

    if (
      merged.durationMs === undefined &&
      typeof merged.startedAt === "number" &&
      typeof merged.completedAt === "number"
    ) {
      merged.durationMs = merged.completedAt - merged.startedAt;
    }

    if (existingIndex >= 0) {
      const updated = [...this.metadata.toolCalls];
      updated[existingIndex] = merged;
      return updated.slice(-MAX_FEEDBACK_TOOL_CALLS);
    }

    return [...this.metadata.toolCalls, merged].slice(-MAX_FEEDBACK_TOOL_CALLS);
  }

  protected emit(event: string, data: unknown): void {
    const handlers = this.eventListeners.get(event);
    if (handlers) {
      handlers.forEach((h) => h(data));
    }
  }
}
